import React from 'react';
import { CaretDown, CaretRight, Folder, FolderOpen, HardDrives } from '@phosphor-icons/react';

export interface OrdnerKnoten {
  id: string;
  name: string;
  kinder: readonly OrdnerKnoten[];
  /** Dateien in diesem Ordner inkl. Unterordner (0 = kein Zähler). */
  zaehler?: number;
  /** Farbe von Punkt/Icon (Hauptordner-Farbe). */
  farbe?: string;
}

/**
 * Ordnerbaum (Handoff Dokumente §6.2): Wurzel oben, Ordner mit Farbe und Dateizähler, Unterordner aufklappbar.
 * Höhe/Sticky bestimmt der Aufrufer (Desktop sticky, mobil max. 210 px mit eigenem Scrollen).
 */
export function FolderTree({ wurzel, knoten, offen, onUmschalten, aktivId, onWahl, style, zeilenHoehe = 36 }: {
  wurzel: { id: string; name: string };
  knoten: readonly OrdnerKnoten[];
  offen: ReadonlySet<string>;
  onUmschalten: (id: string) => void;
  aktivId: string | null;
  onWahl: (id: string) => void;
  style?: React.CSSProperties;
  /** Mobil 44 (Hit-Target), sonst 36. */
  zeilenHoehe?: number;
}) {
  const klappBreite = zeilenHoehe >= 44 ? 40 : 28;
  const zeile = (k: OrdnerKnoten, tiefe: number): React.ReactNode => {
    const auf = offen.has(k.id);
    const aktiv = aktivId === k.id;
    const IconKomp = auf || aktiv ? FolderOpen : Folder;
    return (
      <li key={k.id} role="treeitem" aria-expanded={k.kinder.length ? auf : undefined} aria-selected={aktiv} className="flex flex-col">
        <div className="flex items-center" style={{ paddingLeft: tiefe * 14, borderRadius: 10, background: aktiv ? 'var(--fill2)' : 'transparent' }}>
          {k.kinder.length > 0 ? (
            <button type="button" aria-label={auf ? `${k.name} zuklappen` : `${k.name} aufklappen`} onClick={() => onUmschalten(k.id)}
              className="flex items-center justify-center cursor-pointer shrink-0" style={{ width: klappBreite, height: zeilenHoehe, border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
              {auf ? <CaretDown weight="bold" size={11} /> : <CaretRight weight="bold" size={11} />}
            </button>
          ) : <span className="shrink-0" style={{ width: klappBreite }} />}
          <button type="button" onClick={() => onWahl(k.id)} className="immo-hover-flaeche flex items-center flex-1 min-w-0 cursor-pointer text-left"
            style={{ gap: 8, minHeight: zeilenHoehe, padding: '0 8px 0 2px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 13, fontWeight: aktiv ? 800 : 600 }}>
            <IconKomp weight="fill" size={16} color={k.farbe ?? 'var(--acc)'} style={{ flexShrink: 0 }} />
            <span className="flex-1 min-w-0 truncate">{k.name}</span>
            {!!k.zaehler && <span className="immo-zahl shrink-0" style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink3)' }}>{k.zaehler}</span>}
          </button>
        </div>
        {auf && k.kinder.length > 0 && <ul role="group" className="m-0 p-0 list-none flex flex-col">{k.kinder.map(c => zeile(c, tiefe + 1))}</ul>}
      </li>
    );
  };
  return (
    <nav aria-label="Ordnerbaum" className="immo-glas immo-scroll flex flex-col min-w-0" style={{ borderRadius: 26, padding: 8, gap: 2, overflowY: 'auto', ...style }}>
      <button type="button" onClick={() => onWahl(wurzel.id)} className="immo-hover-flaeche flex items-center cursor-pointer text-left"
        style={{ gap: 9, minHeight: Math.max(40, zeilenHoehe), padding: '0 10px', borderRadius: 12, border: 'none', color: 'var(--ink)', fontSize: 13, fontWeight: 800, background: aktivId === wurzel.id ? 'var(--fill2)' : 'transparent' }}>
        <HardDrives weight="fill" size={17} color="var(--acc)" style={{ flexShrink: 0 }} />
        <span className="truncate">{wurzel.name}</span>
      </button>
      <ul role="tree" aria-label={wurzel.name} className="m-0 p-0 list-none flex flex-col">{knoten.map(k => zeile(k, 0))}</ul>
    </nav>
  );
}
